import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Sidebar from "../../../DesignSystem/Sidebar";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";
import InputField from "../../../DesignSystem/Inputs/InputField";
import DateField from "../../../DesignSystem/Inputs/DateField";
import TextArea from "../../../DesignSystem/Inputs/TextArea";
import CustomSelect from "../../../DesignSystem/Dropdowns/CustomSelect";
import FileUpload from "../../../DesignSystem/Upload/FileUpload";
import UploadedFileList from "../../../DesignSystem/Upload/UploadedFileList";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../../DesignSystem/Buttons/SecondaryButton";
import Banner from "../../../DesignSystem/Banner/Banner";
import { useUserPermissions } from "../../../SignIn/UserPermissionsContext";
import { useAdviserListWithUnappointed } from "../../../../context/Adviser/Utils/UnappointedAppointedAdviserEmailList";
import { auth } from "../../../../backend/firebaseConfig";
import { APP_ROUTES } from "../../../../constants/appRoutes";
import ProgressHistory from "../components/ProgressHistory";
import WebsiteDetailsSections from "../components/WebsiteDetailsSections";
import WebsiteAuditLogs from "../components/WebsiteAuditLogs";
import { EMPTY_DOMAIN, EMPTY_WEBSITE, WEBSITE_STATUSES } from "../websiteConstants";
import {
  createWebsite,
  removeWebsiteForm,
  subscribeAuditLogs,
  subscribeProgressHistory,
  subscribeWebsite,
  updateWebsite,
} from "../services/websiteService";
import { filterStaffForStatuses, subscribeStaffJobDescriptions } from "../services/staffAssignmentService";
import "../../../../css/Marketing/FinancialPromotions/FinancialPromotions.css";
import "../../../../css/Marketing/FinancialPromotions/FinancialPromotionsSubmission.css";
import "../../../../css/Marketing/WebsiteCharges.css";
import {ReactComponent as DomainIcon} from '../../../../assets/icons/marketingDashbaord/Domain.svg';
import { ReactComponent as DropdownIcon } from "../../../../assets/icons/paraplanningDashboard/ArrowDropdown.svg";
import { ReactComponent as AddIcon } from "../../../../assets/icons/add.svg";
import { getEstimatedTurnaroundDate, isWebsiteOverdue, isWebsiteTimelineExtended } from "../utils/websiteTimeline";
import { getLiveWebsiteChargeFlag } from "../utils/websiteLiveChargeFlag";



const WebsiteEntryPage = () => {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTab = location.state?.returnTab || "websites";
  const dashboardReturnPath = `${APP_ROUTES.MARKETING_WEBSITES}?tab=${returnTab}`;
  const { userPermissions } = useUserPermissions();
  const editing = Boolean(websiteId);
  const canViewAuditLogs = Array.isArray(userPermissions) && userPermissions.some(
    (permission) => String(permission || "").trim().toLowerCase().includes("administrator"),
  );
  const [formData, setFormData] = useState(() => ({
    ...EMPTY_WEBSITE,
    estimatedTurnaroundDate: getEstimatedTurnaroundDate(),
  }));
  const [file, setFile] = useState(null);
  const [history, setHistory] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(editing);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState(
    location.state?.successMessage || "",
  );
  const [validationErrors, setValidationErrors] = useState({});
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [staffOptions, setStaffOptions] = useState([]);
  const [staffLoading, setStaffLoading] = useState(true);
  const { advisers, loadingAdvisers } = useAdviserListWithUnappointed();
  const adviserEmailOptions = advisers.map((adviser) => ({
    id: adviser.email,
    name: `${adviser.email}${adviser.name ? ` (${adviser.name})` : ""}`,
  }));

  useEffect(() => {
    if (!successMessage) return undefined;
    const timeoutId = window.setTimeout(() => setSuccessMessage(""), 5000);
    return () => window.clearTimeout(timeoutId);
  }, [successMessage]);

  useEffect(
    () =>
      subscribeStaffJobDescriptions(
        (staff) => {
          const relevantStaff = filterStaffForStatuses(
            staff,
            WEBSITE_STATUSES.map((status) => status.id),
          );
          setStaffOptions(relevantStaff);
          setStaffLoading(false);
        },
        () => setStaffLoading(false),
      ),
    [],
  );

  useEffect(() => {
    if (!websiteId) return undefined;
    const unsubscribeWebsite = subscribeWebsite(
      websiteId,
      (website) => {
        if (!website) {
          navigate(dashboardReturnPath, { replace: true, state: { activeTab: returnTab } });
          return;
        }
        setFormData({
          ...EMPTY_WEBSITE,
          ...website,
          estimatedTurnaroundDate:
            String(website.estimatedTurnaroundDate || "").trim() ||
            getEstimatedTurnaroundDate(website.dateAdviserInformed),
          domains: Array.isArray(website.domains)
            ? website.domains.map((domain, index) => ({
                ...EMPTY_DOMAIN,
                ...domain,
                registrar: String(domain.registrar || "").trim() || "Namecheap",
                markup: String(domain.markup ?? "").trim() || "20",
                design: {
                  ...EMPTY_DOMAIN.design,
                  ...(domain.design || (index === 0 ? website.design : {})),
                },
                hosting: {
                  ...EMPTY_DOMAIN.hosting,
                  ...(domain.hosting || (index === 0 ? website.hosting : {})),
                },
              }))
            : [],
        });
        setLoading(false);
      },
      () => { setError("We couldn't load this website entry."); setLoading(false); },
    );
    const unsubscribeHistory = subscribeProgressHistory(websiteId, setHistory, () => {});
    const unsubscribeAuditLogs = canViewAuditLogs
      ? subscribeAuditLogs(
          websiteId,
          (logs) => { setAuditLogs(logs); setAuditLoading(false); },
          () => setAuditLoading(false),
        )
      : () => {};
    if (!canViewAuditLogs) setAuditLoading(false);
    return () => { unsubscribeWebsite(); unsubscribeHistory(); unsubscribeAuditLogs(); };
  }, [websiteId, navigate, canViewAuditLogs, dashboardReturnPath, returnTab]);

  const updateField = (field) => (event) =>
    setFormData((current) => ({ ...current, [field]: event.target.value }));

  const updateDateAdviserInformed = (event) => {
    const dateAdviserInformed = event.target.value;
    setFormData((current) => ({
      ...current,
      dateAdviserInformed,
      estimatedTurnaroundDate: getEstimatedTurnaroundDate(dateAdviserInformed),
    }));
  };

  const updateAdviserEmail = (event) => {
    const adviserEmail = event.target.value;
    const selectedAdviser = advisers.find(
      (adviser) =>
        String(adviser.email || "").trim().toLowerCase() ===
        String(adviserEmail || "").trim().toLowerCase(),
    );
    const adviserName = [
      selectedAdviser?.firstName,
      selectedAdviser?.lastName,
    ]
      .filter(Boolean)
      .join(" ")
      .trim();

    setFormData((current) => ({
      ...current,
      adviserEmail,
      adviserName,
      arTsName: selectedAdviser?.brandName || "",
    }));
  };

  const addDomain = () => {
    setFormData((current) => ({
      ...current,
      domains: [
        ...(current.domains || []),
        {
          ...EMPTY_DOMAIN,
          design: { ...EMPTY_DOMAIN.design },
          hosting: { ...EMPTY_DOMAIN.hosting },
        },
      ],
    }));
    setDetailsOpen(true);
  };

  const getAuditActor = () => ({
    uid: auth.currentUser?.uid || "",
    email: auth.currentUser?.email || localStorage.getItem("userEmail") || "",
    displayName: auth.currentUser?.displayName || "",
  });

  const validate = () => {
    const errors = {};
    if (!formData.dateStarted || !String(formData.adviserName || "").trim()) {
      errors.required = "Date Entry Created and Adviser Name are required.";
    }
    if (!formData.dateAdviserInformed) {
      errors.dateAdviserInformed = "Date Adviser Informed is required.";
    }
    if (!formData.estimatedTurnaroundDate) {
      errors.estimatedTurnaroundDate = "Estimated Turnaround Date is required.";
    }

    const email = String(formData.adviserEmail || "").trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.adviserEmail = "Enter a valid adviser email address.";
    }

    const isInteger = (value) => !String(value ?? "").trim() || /^\d+$/.test(String(value).trim());
    const isDecimal = (value) => !String(value ?? "").trim() || /^(?:\d+|\d*\.\d+)$/.test(String(value).trim());
    const isLive = String(formData.status || "").trim().toLowerCase() === "live";
    const londonDateParts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London", year: "numeric", month: "2-digit",
    }).formatToParts(new Date());
    const currentMonthStart = `${londonDateParts.find((part) => part.type === "year").value}-${londonDateParts.find((part) => part.type === "month").value}-01`;
    if (isLive && !formData.domains.length) {
      errors.domains = "Add a domain and complete its design and hosting details before saving as Live.";
    }

    formData.domains.forEach((domain, index) => {
      const prefix = `domains.${index}`;
      if (!isInteger(domain.numberOfYears)) {
        errors[`${prefix}.numberOfYears`] = `Domain ${index + 1}: No of Years must be a whole number.`;
      }
      [
        ["amountPaid", "Amount Paid"],
        ["markup", "Markup"],
      ].forEach(([field, label]) => {
        if (!isDecimal(domain[field])) {
          errors[`${prefix}.${field}`] = `Domain ${index + 1}: ${label} must be a valid number.`;
        }
      });
      if (!isDecimal(domain.design?.amountToCharge)) {
        errors[`${prefix}.design.amountToCharge`] = `Domain ${index + 1}: Design Amount To Charge must be a valid number.`;
      }
      if (!isInteger(domain.design?.chargeAcross)) {
        errors[`${prefix}.design.chargeAcross`] = `Domain ${index + 1}: Charge Across must be a whole number.`;
      }
      if (!isDecimal(domain.hosting?.monthlyCharge)) {
        errors[`${prefix}.hosting.monthlyCharge`] = `Domain ${index + 1}: Monthly Charge must be a valid number.`;
      }
      if (isLive) {
        [
          ["design.liveDate", domain.design?.liveDate, "Live Date"],
          ["design.amountToCharge", domain.design?.amountToCharge, "Design Amount To Charge"],
          ["design.chargeAcross", domain.design?.chargeAcross, "Charge Across"],
          ["hosting.monthlyCharge", domain.hosting?.monthlyCharge, "Hosting Monthly Charge"],
        ].forEach(([field, value, label]) => {
          if (!String(value ?? "").trim()) {
            errors[`${prefix}.${field}`] = `Domain ${index + 1}: ${label} is required when status is Live.`;
          }
        });
        if (domain.design?.liveDate && domain.design.liveDate < currentMonthStart) {
          errors[`${prefix}.design.liveDate`] = `Domain ${index + 1}: Live Date cannot be in a previous month.`;
        }
      }
    });

    setValidationErrors(errors);
    if (Object.keys(errors).some((field) => field.startsWith("domains"))) setDetailsOpen(true);
    const messages = [...new Set(Object.values(errors))];
    setError(messages.join(" "));
    return messages.length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setError("");
    setSuccessMessage("");
    setSaving(true);
    try {
      const actor = getAuditActor();
      if (editing) {
        await updateWebsite({ websiteId, formData, file, actor });
        navigate(dashboardReturnPath, {
          replace: true,
          state: { activeTab: returnTab, successMessage: "Website changes saved successfully." },
        });
        return;
      } else {
        await createWebsite({ formData, file, actor });
        const message = "Website entry saved successfully.";
        navigate(dashboardReturnPath, {
          replace: true,
          state: { activeTab: returnTab, successMessage: message },
        });
        return;
      }
    } catch (saveError) {
      setError(saveError.message || "We couldn't save this website entry.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="Main-Page"><Sidebar /><main className="Main-Container website-loading">Loading website…</main></div>;

  const websiteIsOverdue = isWebsiteOverdue(formData);
  const websiteTimelineIsExtended = isWebsiteTimelineExtended(formData);
  const liveWebsiteFlag = getLiveWebsiteChargeFlag(formData);

  return (
    <div className="Main-Page">
      <Helmet>
        <title>{editing ? "Website Details" : "Add New Website Entry"}</title>
      </Helmet>
      <Sidebar />
      <main className="Main-Container fp-submission-container">
        <Breadcrumb
          items={[
            { label: "Marketing", to: APP_ROUTES.DEPARTMENT_MARKETING },
            {
              label: "design⁴",
              to: dashboardReturnPath,
            },
            { label: editing ? "Website Details" : "Add New Entry" },
          ]}
        />
        <form onSubmit={handleSubmit}>
          <header className="fp-header-new website-entry-sticky-header row-space-between gap-s">
            <div className="FP_H3">
              {editing ? "Website Details" : "Add New Entry"}
            </div>
            <div className="row align-center gap-s">
              <SecondaryButton
                type="button"
                // className="website-cancel"
                label="Cancel"
                onClick={() => navigate(dashboardReturnPath, { state: { activeTab: returnTab } })}
              />
              <PrimaryButton
                label={saving ? "Saving…" : editing ? "Save Changes" : "Add Entry"}
                disabled={saving}
              />
            </div>
          </header>
          {successMessage && (
            <div className="website-success-banner">
              <Banner type="success" message={successMessage} />
            </div>
          )}
          {error ? (
            <div className="website-success-banner">
              <Banner type="error" message={error} />
            </div>
          ) : null}
          {liveWebsiteFlag ? (
            <div className="website-success-banner">
              <Banner type="warning" message={liveWebsiteFlag} />
            </div>
          ) : null}
          <div className="website-top-grid">
            <section className={`fp-form-container website-main-form${websiteIsOverdue ? " website-overdue-card" : websiteTimelineIsExtended ? " website-extended-card" : ""}`}>
              <div className="website-fields website-fields--two">
                <DateField
                  label="Date Entry Created"
                  required
                  readOnly
                  error={Boolean(validationErrors.required && !formData.dateStarted)}
                  value={formData.dateStarted}
                  onChange={updateField("dateStarted")}
                />
                <DateField
                  label="Date Adviser Informed"
                  required
                  error={Boolean(validationErrors.dateAdviserInformed)}
                  value={formData.dateAdviserInformed}
                  onChange={updateDateAdviserInformed}
                />
                {/* <div /> */}
                <InputField
                  label="Adviser Name"
                  required
                  placeholder="Adviser Name"
                  error={Boolean(validationErrors.required && !String(formData.adviserName || "").trim())}
                  value={formData.adviserName}
                  onChange={updateField("adviserName")}
                />
                <CustomSelect
                  label="Adviser Email"
                  options={adviserEmailOptions}
                  placeholder={loadingAdvisers ? "Loading advisersâ€¦" : "Select Adviser Email"}
                  value={formData.adviserEmail}
                  error={Boolean(validationErrors.adviserEmail)}
                  onChange={updateAdviserEmail}
                  disabled={loadingAdvisers}
                  clearable
                />
                <InputField
                  label="AR/TS Name"
                  placeholder="AR/TS Name"
                  value={formData.arTsName}
                  onChange={updateField("arTsName")}
                />
                <DateField
                  label="Estimated Turnaround Date"
                  required
                  value={formData.estimatedTurnaroundDate}
                  error={Boolean(validationErrors.estimatedTurnaroundDate)}
                  onChange={updateField("estimatedTurnaroundDate")}
                />
                <CustomSelect
                  label="Status"
                  options={WEBSITE_STATUSES}
                  searchable={false}
                  clearable
                  value={formData.status}
                  onChange={updateField("status")}
                />
                <CustomSelect
                  label="Assigned To"
                  options={staffOptions}
                  value={formData.assignedTo}
                  onChange={updateField("assignedTo")}
                  placeholder={staffLoading ? "Loading staff…" : "Assigned To"}
                  disabled={staffLoading}
                />
              </div>
              <div className="website-upload-block">
                <span className="website-field-label Content-Small primary">
                  Upload Details
                </span>
                <small className="FP_Help_Text black">
                  Upload Website Form
                </small>
                <FileUpload
                  description="Drag & drop the website form here or"
                  browseText="Browse"
                  accept=".pdf,.doc,.docx"
                  multiple={false}
                  files={file ? [file] : []}
                  onFilesChange={setFile}
                />
                <UploadedFileList
                  files={
                    file
                      ? [file]
                      : formData.websiteForm
                        ? [formData.websiteForm]
                        : []
                  }
                  onRemove={async (selectedFile) => {
                    if (selectedFile instanceof File) {
                      setFile(null);
                      return;
                    }
                    if (editing) {
                      await removeWebsiteForm(
                        websiteId,
                        formData.websiteForm,
                        getAuditActor(),
                      );
                    }
                    setFormData((current) => ({
                      ...current,
                      websiteForm: null,
                    }));
                  }}
                />
              </div>
              <TextArea
                label="Notes"
                rows={2}
                placeholder="Additional Notes"
                value={formData.notes}
                onChange={updateField("notes")}
              />
            </section>
            <ProgressHistory items={history} loading={false} />
          </div>

          <section className="website-collapsible website-card">
            <div className="website-collapsible__header space-between-row FP_Content_Semibold">
              <span className="website-section-heading__label row align-center text-align-left gap-s">
                <DomainIcon /> Domain Details
              </span>
              <div className="row align-center gap-s">
                <button
                  type="button"
                  className="website-domain-add row align-center gap-s FP_Content"
                  onClick={addDomain}
                >
                  <AddIcon aria-hidden />
                  Add domain
                </button>
                <button
                  type="button"
                  className="website-collapse-toggle"
                  onClick={() => setDetailsOpen((open) => !open)}
                  aria-label={detailsOpen ? "Collapse domain details" : "Expand domain details"}
                  aria-expanded={detailsOpen}
                >
                  <DropdownIcon className={detailsOpen ? "website-chevron website-chevron--open" : "website-chevron"} />
                </button>
              </div>
            </div>
            {detailsOpen && (
              <WebsiteDetailsSections
                formData={formData}
                setFormData={setFormData}
                validationErrors={validationErrors}
              />
            )}
          </section>

          {editing && canViewAuditLogs && (
            <WebsiteAuditLogs logs={auditLogs} loading={auditLoading} />
          )}

        </form>
      </main>
    </div>
  );
};

export default WebsiteEntryPage;
